import { useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Image as ImageIcon, Upload } from "lucide-react";

export default function ProjectSettingsForm({ project, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    name: project.name || "",
    location: project.location || "",
    budget: project.budget ?? "",
    surface: project.surface ?? "",
    description: project.description || "",
    cover_path: project.cover_path || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onUploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_path: data.path }));
      toast.success("Portada lista. Pulsa «Guardar cambios» para aplicarla.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la imagen.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.location.trim()) return toast.error("Nombre y municipio son obligatorios.");
    setSaving(true);
    try {
      const r = await api.put(`/projects/${project.project_id}`, {
        name: form.name.trim(),
        location: form.location.trim(),
        budget: form.budget === "" ? null : Number(form.budget),
        surface: form.surface === "" ? null : Number(form.surface),
        description: form.description.trim() || null,
        cover_path: form.cover_path || "",
      });
      onSaved(r.data);
      toast.success("Proyecto actualizado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/projects/${project.project_id}`);
      toast.success("Proyecto eliminado.");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el proyecto.");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="project-settings">
      <div className="lg:col-span-8 rounded-2xl border border-border bg-card p-6 sm:p-7 space-y-4">
        <h3 className="text-lg font-semibold tracking-tight">Datos del proyecto</h3>
        <div className="space-y-1.5">
          <Label htmlFor="s-name">Nombre de la vivienda *</Label>
          <Input id="s-name" data-testid="settings-name-input" value={form.name} onChange={set("name")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="s-loc">Municipio / Provincia *</Label>
          <Input id="s-loc" data-testid="settings-location-input" value={form.location} onChange={set("location")} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="s-bud">Presupuesto estimado (€)</Label>
            <Input id="s-bud" type="number" data-testid="settings-budget-input" value={form.budget} onChange={set("budget")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-surf">Superficie (m²)</Label>
            <Input id="s-surf" type="number" data-testid="settings-surface-input" value={form.surface} onChange={set("surface")} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="s-desc">Descripción</Label>
          <Textarea id="s-desc" rows={3} data-testid="settings-description-input" value={form.description} onChange={set("description")} />
        </div>
        <div className="space-y-2 pt-1">
          <Label>Portada (imagen de la vivienda)</Label>
          <div className="flex items-center gap-4">
            <div className="h-20 w-32 rounded-lg border border-border bg-secondary/40 overflow-hidden flex items-center justify-center shrink-0" data-testid="settings-cover-preview">
              {form.cover_path
                ? <img src={`${API}/files/${form.cover_path}`} alt="Portada" className="h-full w-full object-cover" />
                : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="space-y-2">
              <label className="inline-flex">
                <input type="file" accept="image/*" onChange={onUploadCover} className="hidden" data-testid="settings-cover-input" />
                <span className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 cursor-pointer hover:bg-secondary">
                  <Upload className="h-4 w-4" /> {uploading ? "Subiendo…" : (form.cover_path ? "Cambiar portada" : "Subir portada")}
                </span>
              </label>
              {form.cover_path && (
                <Button type="button" variant="ghost" size="sm" className="ml-2 text-destructive hover:text-destructive gap-1.5" onClick={() => setForm((f) => ({ ...f, cover_path: "" }))} data-testid="settings-cover-remove">
                  <Trash2 className="h-4 w-4" /> Quitar
                </Button>
              )}
              <p className="text-[11px] text-muted-foreground">Se mostrará en la cabecera del proyecto. JPG o PNG, máx. 8 MB.</p>
            </div>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full h-11 px-6" data-testid="settings-save-button">
          {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      <div className="lg:col-span-4 rounded-2xl border border-destructive/30 bg-card p-6 h-fit">
        <h3 className="text-lg font-semibold tracking-tight text-destructive">Zona delicada</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-5">Eliminar el proyecto borra sus tareas, usuarios y conversaciones. No se puede deshacer.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-full gap-2 border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="delete-project-button">
              <Trash2 className="h-4 w-4" /> Eliminar proyecto
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar "{project.name}"?</AlertDialogTitle>
              <AlertDialogDescription>Se borrará todo el contenido del proyecto de forma permanente.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
              <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={remove} data-testid="confirm-delete-project-button">
                Sí, eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
